import { DeleteOutlined, LockOutlined } from '@ant-design/icons';
import {
  Button,
  Empty,
  Form,
  InputNumber,
  Select,
  Tooltip,
  Typography,
} from 'antd';
import cn from 'classnames';

import { ConfirmDeletingElement } from '@app/components/ConfirmDeletingElement';
import {
  type SubscriptionSaleOption,
  useSubscriptionSaleItems,
} from '@app/components/RecordModal/components/RecordDetails/components/SubscriptionSaleItems/hooks';
import type {
  BranchUserOption,
  SubscriptionSaleDraftItem,
} from '@app/components/RecordModal/components/RecordDetails/types';
import { NumberInputWithMask } from '@app/components/RecordModal/components/RecordDetails/ui/NumberInputWithMask';
import { WithTooltip } from '@app/components/WithTooltip';
import { divideAmount, withTestId } from '@app/utils';

import type { FC } from 'react';

import './SubscriptionSaleItems.scss';

const { Item } = Form;
const { Text } = Typography;

const SELECT_SUBSCRIPTION_FIRST = 'Сначала выберите абонемент';
const PAID_SUBSCRIPTION_HINT = 'Абонемент оплачен: изменить и удалить нельзя';
const SOLD_SUBSCRIPTION_HINT = 'Абонемент продан: можно изменить только скидку';
const DELETE_SUBSCRIPTION_LABEL = 'Удалить абонемент';

interface SubscriptionSaleItemsProps {
  branchUserOptions: BranchUserOption[];
  activeBranchId: number | null;
  /** Значение поля формы `subscriptions` (передаёт Form.Item) */
  items?: SubscriptionSaleDraftItem[];
  onChange?(items: SubscriptionSaleDraftItem[]): void;
  disabled?: boolean;
}

/**
 * Блок «Абонементы» в «Деталях записи»: продажа абонемента через запись.
 * Устроен как блок товаров (ProductSaleItems) и живёт в форме записи полем
 * `subscriptions`.
 */
export const SubscriptionSaleItems: FC<SubscriptionSaleItemsProps> = ({
  branchUserOptions,
  activeBranchId,
  items = [],
  onChange,
  disabled,
}) => {
  const {
    rows,
    subscriptionOptions,
    isSubscriptionsLoading,
    totalQuantity,
    totalAmount,
    addItemHandler,
    deleteItemHandler,
    isDeleteConfirmOpen,
    deleteConfirmText,
    confirmDeleteHandler,
    deleteModalOpenChangeHandler,
    subscriptionChangeHandler,
    sellerChangeHandler,
    discountCurrencyChangeHandler,
    discountPercentChangeHandler,
  } = useSubscriptionSaleItems({ items, onChange, activeBranchId });

  const addButton = (
    <Button
      type="primary"
      className="SubscriptionSaleItems__add-button"
      onClick={() => void addItemHandler()}
      disabled={disabled}
      data-testid="add-subscription-sale-item"
    >
      Добавить абонемент
    </Button>
  );

  if (rows.length === 0) {
    return <div className="SubscriptionSaleItems">{addButton}</div>;
  }

  return (
    <div className="SubscriptionSaleItems">
      <div className="SubscriptionSaleItems__scroll-wrapper">
        <div className="SubscriptionSaleItems__items">
          <div className="SubscriptionSaleItems__header">
            <Text strong>Название</Text>
            <Text strong>Продавец</Text>
            <Text strong>Кол-во</Text>
            <Text strong>Цена</Text>
            <Text strong>Скидка</Text>
            <Text strong className="SubscriptionSaleItems__header-total">
              К оплате
            </Text>
            <div />
          </div>
          {rows.map(({ item, total, isSold, isPaid, isEditable }) => {
            const isSubscriptionSelected = Boolean(item.subscriptionId);
            const isSoldUnpaid = isSold && !isPaid;
            const isSellerDisabled =
              disabled || isSold || !isSubscriptionSelected;
            const isDiscountDisabled =
              disabled || !isEditable || !isSubscriptionSelected;

            return (
              <div
                className={cn('SubscriptionSaleItems__item', {
                  'SubscriptionSaleItems__item--sold': isSold,
                })}
                key={item.id}
              >
                <Item
                  label="Название"
                  className="SubscriptionSaleItems__field SubscriptionSaleItems__field--name"
                >
                  <WithTooltip
                    show={isSoldUnpaid}
                    title={SOLD_SUBSCRIPTION_HINT}
                  >
                    <Select<number, SubscriptionSaleOption>
                      showSearch
                      optionFilterProp="label"
                      placeholder="Выберите абонемент"
                      options={subscriptionOptions}
                      value={item.subscriptionId}
                      disabled={disabled || isSold}
                      loading={isSubscriptionsLoading}
                      data-testid="subscription-sale-subscription"
                      onChange={(value) =>
                        subscriptionChangeHandler(item.id, value)
                      }
                      labelRender={() => item.name || 'Абонемент без названия'}
                      optionRender={({ data }) => (
                        <div className="SubscriptionSaleItems__option">
                          <Text strong>
                            {data.label || 'Абонемент без названия'}
                          </Text>
                          <span>{divideAmount(data.price)}</span>
                        </div>
                      )}
                      notFoundContent={
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description="Абонементы не найдены. Создайте абонемент в реестре"
                        />
                      }
                    />
                  </WithTooltip>
                  {isSold && (
                    <Text
                      type="secondary"
                      className="SubscriptionSaleItems__status"
                      data-testid="subscription-sale-status"
                    >
                      {isPaid ? (
                        <>
                          <LockOutlined /> Оплачен
                        </>
                      ) : (
                        'Продан'
                      )}
                    </Text>
                  )}
                </Item>
                <WithTooltip
                  show={!isSubscriptionSelected || isSoldUnpaid}
                  title={
                    isSoldUnpaid
                      ? SOLD_SUBSCRIPTION_HINT
                      : SELECT_SUBSCRIPTION_FIRST
                  }
                >
                  <Item
                    label="Продавец"
                    className="SubscriptionSaleItems__field"
                  >
                    <Select
                      options={branchUserOptions}
                      value={item.sellerId}
                      disabled={isSellerDisabled}
                      showSearch
                      allowClear
                      optionFilterProp="label"
                      data-testid="subscription-sale-seller"
                      onChange={(value) => sellerChangeHandler(item.id, value)}
                      popupRender={withTestId(
                        'subscription-sale-seller-dropdown'
                      )}
                    />
                  </Item>
                </WithTooltip>
                <Item label="Кол-во" className="SubscriptionSaleItems__field">
                  <InputNumber
                    value={1}
                    disabled
                    data-testid="subscription-sale-quantity"
                  />
                </Item>
                <Item label="Цена" className="SubscriptionSaleItems__field">
                  <NumberInputWithMask
                    value={item.price}
                    disabled
                    dataTestId="subscription-sale-price"
                  />
                </Item>
                <WithTooltip
                  show={!isSubscriptionSelected}
                  title={SELECT_SUBSCRIPTION_FIRST}
                >
                  <Item
                    label="Скидка"
                    className="SubscriptionSaleItems__field SubscriptionSaleItems__field--discount"
                  >
                    <NumberInputWithMask
                      value={item.discountCurrency}
                      disabled={isDiscountDisabled}
                      dataTestId="subscription-sale-discount-price"
                      onChange={(value) =>
                        discountCurrencyChangeHandler(item.id, value)
                      }
                    />
                    <NumberInputWithMask
                      value={item.discountPercent}
                      disabled={isDiscountDisabled}
                      dataTestId="subscription-sale-discount-percent"
                      suffix="%"
                      onChange={(value) =>
                        discountPercentChangeHandler(item.id, value)
                      }
                    />
                  </Item>
                </WithTooltip>
                <div className="SubscriptionSaleItems__total">
                  <Text strong className="SubscriptionSaleItems__total-label">
                    К оплате
                  </Text>
                  <Text data-testid="subscription-sale-total">
                    {divideAmount(total)}
                  </Text>
                </div>
                {isPaid ? (
                  <Tooltip title={PAID_SUBSCRIPTION_HINT}>
                    <LockOutlined
                      className="SubscriptionSaleItems__lock"
                      data-testid="subscription-sale-lock"
                    />
                  </Tooltip>
                ) : (
                  <Tooltip title={DELETE_SUBSCRIPTION_LABEL}>
                    <Button
                      icon={<DeleteOutlined />}
                      aria-label={DELETE_SUBSCRIPTION_LABEL}
                      onClick={() => deleteItemHandler(item.id)}
                      disabled={disabled}
                      data-testid="delete-subscription-sale-item"
                    />
                  </Tooltip>
                )}
              </div>
            );
          })}
          <div className="SubscriptionSaleItems__item SubscriptionSaleItems__item--summary">
            <Text strong>Итого:</Text>
            <div />
            <Text className="SubscriptionSaleItems__summary-quantity">
              {divideAmount(totalQuantity)} шт
            </Text>
            <div />
            <div />
            <div className="SubscriptionSaleItems__total">
              <Text>{divideAmount(totalAmount)}</Text>
            </div>
            <div />
          </div>
        </div>
      </div>
      <div className="SubscriptionSaleItems__footer">{addButton}</div>
      <ConfirmDeletingElement
        modalOpen={isDeleteConfirmOpen}
        setModalOpen={deleteModalOpenChangeHandler}
        handleDelete={confirmDeleteHandler}
        title="Удаление проданного абонемента"
        content={deleteConfirmText}
        modalDataTestId="subscription-sale-delete-confirm"
      />
    </div>
  );
};
